import React, { useState, useEffect, useMemo } from 'react';
import SearchableDropdown from '../SearchableDropdown';

// Modal for creating a category with live suggestions
export default function CategoryModal({ open, onClose, onCreate, parentOptions = [], error, setError, creating }) {
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    parentId: '',
    status: 'active',
    image: '',
    tags: '',
    keywords: '',
    metaTitle: '',
    metaDescription: ''
  });
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) {
      setForm({
        name: '', slug: '', description: '', parentId: '', status: 'active',
        image: '', tags: '', keywords: '', metaTitle: '', metaDescription: ''
      });
      setQuery('');
      setError && setError('');
    }
  }, [open, setError]);

  const suggestions = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return parentOptions.filter((opt) => opt.label.toLowerCase().includes(q)).slice(0, 6);
  }, [query, parentOptions]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg bg-white dark:bg-admin-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-admin-slate-700 flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-admin-slate-700 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create Category</h3>
          <button type="button" className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" onClick={onClose}>×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  const slug = name.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
                  setForm((f) => ({ ...f, name, slug }));
                  setQuery(name);
                  if (error) setError('');
                }}
                placeholder="e.g., Surgical Instruments"
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-admin-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-admin-slate-700 dark:text-white"
              />
              {query && (
                <div className="mt-2 border border-gray-200 dark:border-admin-slate-600 rounded-md max-h-40 overflow-auto">
                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-admin-slate-600">Similar existing categories</div>
                  {suggestions.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No close matches</div>
                  ) : (
                    suggestions.map((s) => (
                      <div key={s._id || s.value} className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                        {s.label}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Slug</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-admin-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-admin-slate-700 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-admin-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-admin-slate-700 dark:text-white"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Parent category (optional)</label>
              <SearchableDropdown
                label={null}
                options={[{ value: '', label: 'No parent (top level)' }, ...parentOptions.map((o) => ({ value: o._id || o.value, label: o.label }))]}
                value={form.parentId}
                onChange={(value) => setForm((f) => ({ ...f, parentId: value }))}
                placeholder="Select parent category..."
                emptyMessage="No categories available"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-admin-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-admin-slate-700 dark:text-white"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-gray-200 dark:border-admin-slate-700 flex items-center justify-end gap-2 flex-shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 dark:bg-admin-slate-700 text-gray-700 dark:text-gray-300 rounded-md">Cancel</button>
          <button
            type="button"
            disabled={creating}
            onClick={() => onCreate({
              name: form.name,
              parentId: form.parentId,
              slug: form.slug,
              description: form.description,
              status: form.status,
              image: form.image,
              tags: form.tags,
              keywords: form.keywords,
              metaTitle: form.metaTitle,
              metaDescription: form.metaDescription
            })}
            className="px-4 py-2 bg-primary-600 text-white rounded-md disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
